import React, { useState, useEffect } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
	ScatterChart,
	Scatter,
	ZAxis,
	AreaChart,
	Area,
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	PolarRadiusAxis,
	Radar,
} from 'recharts';
import moment from 'moment';
import TableLoading from '../../components/TableLoading';
import { formatCamelCaseToText, formatHyphenatedText, mapTimeRangeToApiFilterAlt3, request } from '../../services/utilities';
import { analyticsAPI, analyticsAPIV2 } from '../../services/constants';
import { count } from 'rxjs';
import { TimeRangeDropdownAlt3 } from '../../components/TimeRangeDropdownAlt3';
import { Statistic } from 'antd';
import SlidingPane from '../../components/SlidingPane';
import { CustomTooltipAlt2 } from '../Doctor/Overview';
// import AntenatalOverview from './AntenatalOverview';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff6e54', '#ffc0cb', '#faad14'];

const NICUOverview = () => {
	const [timeRanges, setTimeRanges] = useState({
		nicuTrends: 'past30Days',
		count: 'past30Days',
		healthState: 'past30Days',
		nicuTopBottom: 'past30Days',
		accomodations: 'past30Days',
		immunizationCount: 'past30Days',
		immunizationByDoctor: 'past30Days',
		immunizationByNurse: 'past30Days',
		immunizationTopBottom: 'past30Days',
		immunizationTrends: 'past30Days',
		staffAverageTime: 'past30Days',
		admissionMetrics: 'past30Days',
		staffRank: 'past30Days',
	});

	const [nicuTrends, setNicuTrends] = useState([]);
	const [topBottom, setTopBottom] = useState([]);
	const [accomodations, setAccomodations] = useState([]);
	const [healthState, setHealthState] = useState([]);
	const [counts, setCounts] = useState([]);
	const [admissionMetrics, setAdmissionMetrics] = useState([]);
	const [staffRank, setStaffRank] = useState([]);

	const [modalAlt2, setModalAlt2] = useState(false);

	const [immunizationCount, setImmunizationCount] = useState(null);
	const [immunizationByDoctor, setImmunizationByDoctor] = useState([]);
	const [immunizationByNurse, setImmunizationByNurse] = useState([]);
	const [topImmunization, setTopImmunization] = useState([]);
	const [bottomImmunization, setBottomImmunization] = useState([]);
	const [immunizationTrends, setImmunizationTrends] = useState([]);
	const [staffAverageTime, setStaffAverageTime] = useState(null);
	const [loadingStates, setLoadingStates] = useState({
		nicuTrends: false,
		nicuTopBottom: false,
		count: false,
		accomodations: false,
		healthState: false,
		immunizationCount: false,
		immunizationByDoctor: false,
		immunizationByNurse: false,
		immunizationTopBottom: false,
		immunizationTrends: false,
		staffAverageTime: false,
		admissionMetrics: false,
		staffRank: false,
	});

	// count
	const fetchNicuCount = async range => {
		setLoadingStates(prev => ({ ...prev, nicuTrends: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/nicu-count?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result;
			setCounts(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch nicu counts:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, count: false }));
		}
	};
	// nicu
	const fetchNicuTrends = async range => {
		setLoadingStates(prev => ({ ...prev, nicuTrends: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/nicu-trends?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name:
						item?.date ||
						item?.year ||
						(dateFilter == 'past-three-years' && moment(item?.month).format('MMMM,YY')) ||
						(item?.month && moment(item?.month).format('MMM')),
					count: parseInt(item?.count),
				}));
			setNicuTrends(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization trends:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, nicuTrends: false }));
		}
	};

	const fetchNicuTopBottom = async range => {
		setLoadingStates(prev => ({ ...prev, nicuTopBottom: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/nicu-top-bottom?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res;
			// ?.sort(() => Math.random() - 0.5)
			// ?.map(item => ({
			//     name: item?.username,
			//     count: parseInt(item?.count),
			// }));
			setTopBottom(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization trends:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, nicuTopBottom: false }));
		}
	};

	const fetchNicuAccomodations = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationByDoctor: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/nicu-accomodation?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.accommodationName || 'Unknown',
					count: parseInt(item?.count),
				}));
			setAccomodations(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization by doctor:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationByDoctor: false }));
		}
	};

	const fetchNicuHealthState = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationByDoctor: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/nicu-health-state?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.health_state || 'Unknown',
					count: parseInt(item?.count),
				}));
			setHealthState(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization by doctor:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationByDoctor: false }));
		}
	};

	// --------------------------------------------------------------------------------------

	// Immunization Count

	const fetchImmunizationCount = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationCount: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-count?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = parseInt(res?.result);
			setImmunizationCount(apiResponse);
		} catch (error) {
			console.error('Failed to fetch immunization count:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationCount: false }));
		}
	};

	// Immunization By Doctor
	const fetchImmunizationByDoctor = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationByDoctor: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-by-staff?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.doctor || 'Unknown',
					count: parseInt(item?.count),
				}));
			setImmunizationByDoctor(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization by doctor:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationByDoctor: false }));
		}
	};

	// Immunization By Nurse
	const fetchImmunizationByNurse = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationByNurse: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-by-nurse?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.doctor || 'Unknown',
					count: parseInt(item?.count),
				}))
				.slice(0, 30);
			setImmunizationByNurse(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization by doctor:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationByNurse: false }));
		}
	};

	// Immunization Top & Bottom
	const fetchImmunizationTopBottom = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationTopBottom: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-top-bottom?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponseTop = res?.top10
				// ?.sort(() => Math.random() - 0.5)
				?.map(x => ({
					name: x?.vaccine,
					count: parseInt(x?.count),
				}));

			const apiResponseBottom = res?.bottom10
				// ?.sort(() => Math.random() - 0.5)
				?.map(x => ({
					name: x?.vaccine,
					count: parseInt(x?.count),
				}));
			setTopImmunization(apiResponseTop);
			setBottomImmunization(apiResponseBottom);
		} catch (error) {
			console.error('Failed to fetch immunization top-bottom:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationTopBottom: false }));
		}
	};

	// Immunization Trends
	const fetchImmunizationTrends = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationTrends: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-trends?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.date || item?.year || item?.month || 'Unknown',
					count: parseInt(item?.count),
				}));
			setImmunizationTrends(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization trends:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationTrends: false }));
		}
	};

	// Staff Average Time
	const fetchStaffAverageTime = async range => {
		setLoadingStates(prev => ({ ...prev, staffAverageTime: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/staff-activity?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.username || 'Unknown',
					count: parseFloat(item?.avgTimeSpent),
				}));
			setStaffAverageTime(apiResponse);
		} catch (error) {
			console.error('Failed to fetch staff average time:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, staffAverageTime: false }));
		}
	};

	// const fetchAdmissionMetrics = async range => {
	// 	setLoadingStates(prev => ({ ...prev, admissionMetrics: true }));
	// 	const dateFilter = mapTimeRangeToApiFilterAlt3(range);
	// 	try {
	// 		const res = await request(`${analyticsAPIV2}/nicu/admission-metrics?dateFilter=${dateFilter}`, 'GET', true);
	// 		const apiResponse = res?.result
	// 		setAdmissionMetrics(apiResponse);
	// 	} catch (error) {
	// 		console.error('Failed to fetch staff average time:', error);
	// 	} finally {
	// 		setLoadingStates(prev => ({ ...prev, admissionMetrics: false }));
	// 	}
	// };

	const fetchAdmissionMetrics = async range => {
		setLoadingStates(prev => ({ ...prev, admissionMetrics: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);

		try {
			const res = await request(`${analyticsAPIV2}/nicu/admission-metrics?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result;

			// ✅ Transform the API data into the desired format
			const formattedData = apiResponse?.map(item => ({
				name: item.accommodationName,
				data: Object.entries(item)
					.filter(([key]) => key !== 'accommodationName') // exclude name field
					.map(([key, value]) => ({
						name: key,
						value: Number(value) || 0, // convert to number safely
					})),
			}));

			setAdmissionMetrics(formattedData);
		} catch (error) {
			console.error('Failed to fetch admission metrics:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, admissionMetrics: false }));
		}
	};

	const fetchStaffRank = async range => {
		setLoadingStates(prev => ({ ...prev, staffRank: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `analytics-v2/get-staff-request-count-by-role?roles=nurse&dateFilter=${dateFilter}&type=nursing`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.map(x => ({
				...x,
				name: x?.username || 'unknown',
				role: x?.role,
			}));
			console.log(apiResponse, 'staff rank...');
			setStaffRank(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch staff rank:', error);
			setStaffRank([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, staffRank: false }));
		}
	};
	// Initial data load
	useEffect(() => {
		fetchNicuTrends(timeRanges.nicuTrends);
		fetchNicuTopBottom(timeRanges.nicuTopBottom);
		fetchNicuAccomodations(timeRanges.accomodations);
		fetchNicuHealthState(timeRanges.healthState);
		fetchNicuCount(timeRanges.count);
		fetchAdmissionMetrics(timeRanges.admissionMetrics);
		fetchStaffRank(timeRanges.staffRank);
	}, []);

	// Handle time range changes
	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'nicuTrends':
				fetchNicuTrends(range);
				break;
			case 'count':
				fetchNicuCount(range);
				fetchAdmissionMetrics(range);
				break;
			case 'nicuTopBottom':
				fetchNicuTopBottom(range);
				break;
			case 'accomodations':
				fetchNicuAccomodations(range);
				break;
			case 'healthState':
				fetchNicuHealthState(timeRanges.healthState);
			case 'immunizationCount':
				fetchImmunizationCount(range);
				break;
			case 'immunizationByDoctor':
				fetchImmunizationByDoctor(range);
				break;
			case 'immunizationByNurse':
				fetchImmunizationByNurse(range);
				break;
			case 'immunizationTopBottom':
				fetchImmunizationTopBottom(range);
				break;
			case 'immunizationTrends':
				fetchImmunizationTrends(range);
				break;
			case 'staffAverageTime':
				fetchStaffAverageTime(range);
				break;
			case 'admissionMetrics':
				fetchAdmissionMetrics(range);
				break;
			default:
				break;
		}
	};

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const renderCustomLabelAlt2 = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString() + 'hr'}
			</text>
		);
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	const CustomToolTipAlt = ({ active, payload, label }) => {
		if (!active || !payload?.length) return null;

		const data = payload[0]?.payload;

		return (
			<div className="custom-tooltip" style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: 10 }}>
				<p>
					<strong>{String(label).charAt(0).toUpperCase() + String(label).slice(1)}</strong>
				</p>
				<p>Average Turn Around Time: {Number(data?.avgTATMinutes).toLocaleString() + 'mins'}</p>
			</div>
		);
	};

	console.log(admissionMetrics, '--------------------admission metrics');

	const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc0cb', '#faad14'];

	return (
		<div className="content-">
			<div className="content-bo">
				<div className="row">
					{/* Immunization */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> NICU Overview </h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.count} onChange={handleTimeRangeChange} chartId="count" />
							</div>
							<p className="chart-description card p-2">Summary metrics showing overall NICU activity.</p>

							<div className="element-box">
								{loadingStates.count ? (
									<TableLoading />
								) : (
									<>
										<div className="row">
											<div className="col-md-2 element-box el-tablo col-sm-4 col-lg-3">
												{/* <div className="value text-primary" style={{ fontSize: '25px' }}>
												{counts.toLocaleString() || 0}
											</div> */}
												<Statistic
													title={'Total Nicu'}
													value={counts.toLocaleString() || 0}
													valueStyle={{
														fontWeight: 'bold',
														// color: "blue",
													}}
												/>
												<div className="label">Total NICU Count.</div>
											</div>
										</div>

										{admissionMetrics?.map((x, idx) => {
											// Find the total value for this accommodation
											const totalItem = x?.data?.find(item => item.name === 'total');
											const totalValue = Number(totalItem?.value) || 0;

											return (
												<div key={idx} className=" 1-100">
													<h6 className="pl-3 mt-2">{x?.name}</h6>

													<div className="row">
														{x?.data?.map((item, i) => {
															const color = COLORS[i % COLORS.length];
															const percentage =
																item.name === 'total' || totalValue === 0 ? null : ((item.value / totalValue) * 100).toFixed(1); // e.g. 45.2%

															return (
																<div key={i} className="col-4 mb-3">
																	<a className="element-box el-tablo" href="#">
																		<Statistic
																			title={formatCamelCaseToText(item?.name)}
																			value={item?.value}
																			valueStyle={{
																				fontWeight: 'bold',
																				color: color,
																			}}
																		/>
																		{percentage && (
																			<small className="text-muted bold">
																				<strong style={{ color }}>{percentage}%</strong> of Total for &nbsp;
																			</small>
																		)}
																		<small className="text-muted">
																			<strong>{formatHyphenatedText(mapTimeRangeToApiFilterAlt3(timeRanges?.count))}</strong>
																		</small>
																	</a>
																</div>
															);
														})}
													</div>
												</div>
											);
										})}
									</>
								)}
							</div>
						</div>
					</div>

					{/* Immunization Trends */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">NICU Activity Trends</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges.nicuTrends} onChange={handleTimeRangeChange} chartId="nicuTrends" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								This chart visualizes NICU activity (trends) over a selected time, showing analysis of NICU activity.
							</p>

							<div className="element-box">
								{loadingStates.nicuTrends ? (
									<TableLoading />
								) : nicuTrends?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={nicuTrends} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.nicuTrends === 'pastQuarter' ? 1 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#FFBB28"
													fill="#FFBB28"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/*  Top nicu */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">NICU top 10</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges.nicuTopBottom} onChange={handleTimeRangeChange} chartId="nicuTopBottom" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								This chart shows staffs with the highest NICu registrations over a selected time.
							</p>

							<div className="element-box">
								{loadingStates?.nicuTopBottom ? (
									<TableLoading />
								) : topBottom?.top10?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart
												data={topBottom.top10?.map(item => ({
													name: item?.username,
													count: parseInt(item?.count),
												}))}
												margin={{ top: 20, right: 20, left: 10, bottom: 40 }}
											>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Bar dataKey="count" fill="#8884d8" stroke="#8884d8">
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/*  nicu accomodation */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">NICU Accomodation</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges.accomodations} onChange={handleTimeRangeChange} chartId="accomodations" />
							</div>
							<p className="chart-description mb-3 p-2 card">This chart shows NICU accomodation rate.</p>

							<div className="element-box">
								{loadingStates?.accomodations ? (
									<TableLoading />
								) : accomodations?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<PieChart>
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend />
												<Pie
													data={accomodations}
													dataKey="count"
													nameKey="name"
													cx="50%"
													cy="50%"
													outerRadius={120}
													label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
												>
													{accomodations.map((entry, index) => (
														<Cell
															key={`cell-${index}`}
															fill={
																[
																	'#8884d8', // purple
																	'#82ca9d', // green
																	'#ffc658', // yellow
																	'#ff7f50', // coral
																	'#0088fe', // blue
																	'#00c49f', // teal
																	'#ffbb28', // orange
																	'#d0ed57', // lime
																][index % 8]
															}
														/>
													))}
													<LabelList dataKey="count" position="inside" style={{ fontSize: '12px', fill: '#fff' }} />
												</Pie>
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* health state */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">NICU Health state</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges.healthState} onChange={handleTimeRangeChange} chartId="healthState" />
							</div>
							<p className="chart-description mb-3 p-2 card">This chart shows NICU analysis by health state.</p>

							<div className="element-box">
								{loadingStates?.healthState ? (
									<TableLoading />
								) : healthState?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<PieChart>
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend />
												<Pie
													data={healthState}
													dataKey="count"
													nameKey="name"
													cx="50%"
													cy="50%"
													outerRadius={120}
													label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
												>
													{healthState.map((entry, index) => (
														<Cell
															key={`cell-${index}`}
															fill={
																[
																	'#8884d8', // purple
																	'#82ca9d', // green
																	'#ffc658', // yellow
																	'#ff7f50', // coral
																	'#0088fe', // blue
																	'#00c49f', // teal
																	'#ffbb28', // orange
																	'#d0ed57', // lime
																][index % 8]
															}
														/>
													))}
													<LabelList dataKey="count" position="inside" style={{ fontSize: '12px', fill: '#fff' }} />
												</Pie>
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/*Staff Rank */}
					<div className="col-12 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Staff Request Ranking</h6>

							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt2(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdownAlt3 chartId="staffRank" onChange={handleTimeRangeChange} value={timeRanges?.staffRank} />
							</div>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The Staff Request Ranking by Role chart highlights the distribution and frequency of staff requests across different job
								roles, helping identify which roles generate the highest number of requests based on the selected filter.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loadingStates.staffRank ? (
											<TableLoading />
										) : staffRank ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer>
													<LineChart
														data={staffRank.slice(0, 30)}
														layout="horizontal"
														margin={{ top: 20, right: 20, left: 20, bottom: 55 }}
													>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip content={<CustomTooltipAlt2 />} />

														<Line
															type="monotone"
															dataKey="total"
															stroke="#22BB33"
															strokeWidth={2}
															dot={{ r: 2 }}
															activeDot={{ r: 4 }}
														></Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<SlidingPane isOpen={modalAlt2}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModalAlt2(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdownAlt3 value={timeRanges.staffRank} onChange={handleTimeRangeChange} chartId="staffRank" />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Staff Request Ranking</h6>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										The Staff Request Ranking by Role chart highlights the distribution and frequency of staff requests across different job
										roles, helping identify which roles generate the highest number of requests based on the selected filter.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loadingStates.staffRank ? (
															<TableLoading />
														) : staffRank ? (
															<div style={{ width: '100%', height: 400 }}>
																<ResponsiveContainer>
																	<LineChart data={staffRank} layout="horizontal" margin={{ top: 20, right: 20, left: 20, bottom: 55 }}>
																		<CartesianGrid strokeDasharray="3 3" />
																		<XAxis
																			dataKey={'name'}
																			angle={-45}
																			textAnchor="end"
																			fontSize={12}
																			interval={2}
																			tickFormatter={value => {
																				const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																				const capitalized = truncated
																					.toLowerCase()
																					.split(' ')
																					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																					.join(' ');
																				return capitalized;
																			}}
																		/>
																		<YAxis domain={[0, 'auto']} />
																		<Tooltip content={<CustomTooltipAlt2 />} />

																		<Line
																			type="monotone"
																			dataKey="total"
																			stroke="#22BB33"
																			strokeWidth={2}
																			dot={{ r: 2 }}
																			activeDot={{ r: 4 }}
																		></Line>
																	</LineChart>
																</ResponsiveContainer>
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>
				</div>
			</div>
		</div>
	);
};

export default NICUOverview;
